import { useEffect, useState } from 'react'
import { prefersReducedMotion } from '../../scoreCountUp'
import { RULES_SECTIONS, type RulesSectionId } from './content'

/** Height the sticky nav occupies. Kept in step with `--rules-nav-height` in rules.css. */
export const RULES_NAV_HEIGHT = 52

export function rulesSectionElementId(id: RulesSectionId): string {
  return `rules-section-${id}`
}

/**
 * Which section the reader is in.
 *
 * A scroll listener rather than an IntersectionObserver: the answer wanted here is "which section
 * owns the line just under the nav", and an observer answers a different question ("which sections
 * are visible"), which on a page of short sections is usually three of them at once.
 *
 * The document itself scrolls on a pushed page — same as the league detail screen — so this reads
 * viewport-relative tops and picks the last one that has passed the nav.
 */
export function useRulesScrollSpy(): RulesSectionId {
  const [activeId, setActiveId] = useState<RulesSectionId>(RULES_SECTIONS[0].id)

  useEffect(() => {
    let frame = 0
    const update = () => {
      frame = 0
      // A couple of pixels of slack: a section scrolled exactly to the nav line should already
      // count as the active one, and `scroll-margin-top` lands it there to the pixel.
      const line = RULES_NAV_HEIGHT + 2
      let current = RULES_SECTIONS[0].id
      for (const section of RULES_SECTIONS) {
        const el = document.getElementById(rulesSectionElementId(section.id))
        if (!el) continue
        if (el.getBoundingClientRect().top <= line) current = section.id
      }
      setActiveId(current)
    }
    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(update)
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [])

  return activeId
}

/** Scrolls a section under the sticky nav. `scroll-margin-top` in rules.css does the clearing. */
export function scrollToRulesSection(id: RulesSectionId, animate: boolean): void {
  const el = document.getElementById(rulesSectionElementId(id))
  if (!el) return
  el.scrollIntoView({
    behavior: animate && !prefersReducedMotion() ? 'smooth' : 'auto',
    block: 'start',
  })
}
